import { notify } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useEffect, useRef } from 'react';

import { AppVersionWatcher, fetchServedVersion } from './app-version';
import { reloadForChunkError } from './chunk-reload';

/** One toast id: a newer release replaces the open prompt instead of stacking a second one. */
export const APP_UPDATE_TOAST_ID = 'pipe-app-update';

function localStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Only a browser-served build can be stale against its server; native and desktop shells ship
 *  their own bundle, and a build without a commit (tests, storybook) has nothing to compare. */
function isServedWebBuild() {
  return (
    (BUILD_CONFIG.isWeb || BUILD_CONFIG.isMobileWeb) && !!BUILD_CONFIG.appCommit
  );
}

/**
 * Headless, mounted once per app shell (desktop and mobile roots). Offers a reload when the server
 * serves a different release than this tab runs, and reloads once when a code chunk fails to load
 * outside a React error boundary. The prompt never takes focus, stays until closed, is offered once
 * per release, and a dismissed (or reloaded-for) release is never offered again — so it cannot loop.
 */
export const AppFreshness = () => {
  const t = useI18n();
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    const onRejection = (event: PromiseRejectionEvent) => {
      reloadForChunkError(event.reason);
    };
    window.addEventListener('unhandledrejection', onRejection);
    if (!isServedWebBuild()) {
      return () =>
        window.removeEventListener('unhandledrejection', onRejection);
    }

    const watcher: AppVersionWatcher = new AppVersionWatcher({
      runningSha: BUILD_CONFIG.appCommit,
      fetchVersion: () => fetchServedVersion(),
      storage: localStore(),
      onCurrent: () => notify.dismiss(APP_UPDATE_TOAST_ID),
      onNewVersion: sha => {
        const tr = tRef.current;
        notify(
          {
            title: tr['com.affine.pipe.app-update.available'](),
            actions: [
              {
                key: 'reload',
                label: tr['com.affine.pipe.app-update.reload'](),
                buttonProps: { variant: 'primary' },
                onClick: () => window.location.reload(),
              },
            ],
            // Closing it, or reloading from it, retires this release's prompt for good.
            onDismiss: () => watcher.dismiss(sha),
          },
          { id: APP_UPDATE_TOAST_ID, duration: Infinity }
        );
      },
    });
    const stop = watcher.start(window, document);
    return () => {
      stop();
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
};
